import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  Box,
  Divider,
  Badge,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const roleLabel = {
  "tour-owner": "Tour Owner",
  "tour-distributor": "Distributor",
};

const roleStyle = {
  "tour-owner": { bg: "#FFF0F0", color: "#E50000" },
  "tour-distributor": { bg: "#EFF6FF", color: "#3B82F6" },
};

const PAGE_TITLES = {
  "/dashboard": "Dashboard",
  "/tours": "Tour Packages",
  "/tour-listings": "Tour Listings",
  "/manage-booking": "Bookings",
  "/pricing-rule-form": "Pricing Rules",
  "/profile": "My Account",
  "/create-package": "Create Package",
};

const TopNavbar = ({ onMenuClick, user }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleProfile = () => {
    handleMenuClose();
    navigate("/profile");
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    handleMenuClose();
    window.location.href = "/login";
  };

  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase()
    : "?";

  const currentTitle =
    PAGE_TITLES[location.pathname] ??
    (location.pathname.startsWith("/edit-package")
      ? "Edit Package"
      : location.pathname.startsWith("/book")
      ? "Book Tour"
      : location.pathname.startsWith("/package")
      ? "Package Details"
      : "Dashboard");

  const roleBadge = roleStyle[user?.role];

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{
        bgcolor: "#FFFFFF",
        borderBottom: "1px solid #EAECF0",
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Toolbar
        sx={{
          px: { xs: 2, md: 3 },
          minHeight: "64px !important",
          gap: 1,
        }}
      >
        {/* Mobile hamburger */}
        <IconButton
          edge="start"
          aria-label="open navigation"
          onClick={onMenuClick}
          sx={{
            display: { md: "none" },
            mr: 0.5,
            color: "#6B7280",
            "&:hover": { bgcolor: "#F9FAFB" },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Page title */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: "#111827",
              letterSpacing: "-0.015em",
            }}
          >
            {currentTitle}
          </Typography>
        </Box>

        {/* Notification bell */}
        <Tooltip title="Notifications">
          <IconButton
            size="small"
            sx={{
              color: "#6B7280",
              "&:hover": { bgcolor: "#F9FAFB", color: "#111827" },
            }}
          >
            <Badge
              badgeContent={2}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.6rem",
                  minWidth: 15,
                  height: 15,
                  padding: "0 3px",
                },
              }}
            >
              <NotificationsNoneIcon sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Vertical separator */}
        <Box
          sx={{
            width: 1,
            height: 26,
            bgcolor: "#E5E7EB",
            mx: 0.75,
            display: { xs: "none", sm: "block" },
          }}
        />

        {/* User section */}
        <Box
          onClick={handleMenuOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            cursor: "pointer",
            borderRadius: 2,
            px: 1.25,
            py: 0.75,
            border: "1px solid transparent",
            transition: "all 0.15s",
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#E5E7EB",
            },
          }}
        >
          <Avatar
            src={user?.avatarUrl}
            alt={user?.firstName}
            sx={{
              width: 32,
              height: 32,
              fontSize: "0.75rem",
              fontWeight: 700,
              bgcolor: "#E50000",
              color: "#fff",
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Typography
              sx={{
                lineHeight: 1.25,
                color: "#111827",
                fontWeight: 600,
                fontSize: "0.8125rem",
              }}
            >
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography
              sx={{ lineHeight: 1, color: "#6B7280", fontSize: "0.7rem" }}
            >
              {roleLabel[user?.role] ?? user?.role ?? ""}
            </Typography>
          </Box>
          <KeyboardArrowDownIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
        </Box>

        {/* Dropdown menu */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 1.25,
              minWidth: 230,
              border: "1px solid #EAECF0",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              borderRadius: 2,
              overflow: "visible",
            },
          }}
        >
          {/* User card */}
          <Box sx={{ px: 2, py: 1.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                src={user?.avatarUrl}
                sx={{
                  width: 40,
                  height: 40,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  bgcolor: "#E50000",
                  color: "#fff",
                  flexShrink: 0,
                }}
              >
                {initials}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ color: "#111827", whiteSpace: "nowrap" }}
                >
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6B7280",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 140,
                  }}
                >
                  {user?.email}
                </Typography>
              </Box>
            </Box>
            {user?.role && roleBadge && (
              <Box
                sx={{
                  mt: 1.25,
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1,
                  py: 0.35,
                  borderRadius: 1,
                  bgcolor: roleBadge.bg,
                  color: roleBadge.color,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                }}
              >
                {roleLabel[user.role] ?? user.role}
              </Box>
            )}
          </Box>

          <Divider sx={{ borderColor: "#EAECF0" }} />

          <MenuItem
            onClick={handleProfile}
            sx={{
              py: 1.25,
              px: 2,
              fontSize: "0.875rem",
              color: "#374151",
              gap: 1.5,
              "&:hover": { bgcolor: "#F9FAFB" },
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 18, color: "#6B7280" }} />
            View Profile
          </MenuItem>

          <Divider sx={{ borderColor: "#EAECF0" }} />

          <MenuItem
            onClick={handleLogout}
            sx={{
              py: 1.25,
              px: 2,
              fontSize: "0.875rem",
              color: "#EF4444",
              gap: 1.5,
              "&:hover": { bgcolor: "#FFF5F5" },
            }}
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
            Sign Out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopNavbar;
